import { ActionBehavior, ButtonSize, ButtonTitleBarView, CrudViewColumnType, CrudViewFormItemType, HeadingComponentConfig, HeadingType, createDateDisplayFormatFunction } from '@rappider/rappider-components/utils';
import { Validators } from '@angular/forms';
import { EntityConfigMetadata, EntityField } from '../models/entity-config.models';
import { ItemActions } from 'libs/components/src/lib/components/crud-view/model/enums';
import { DEFAULT_CREATE_BUTTON_ICON } from 'libs/components/src/lib/components/crud-view/config/constants';
import { ListMode } from '@rappider/rappider-components';

/**
 * Metadata configuration for the DataSource entity.
 * This object is used by the EntityService to dynamically generate
 * a full CRUD interface (list, create, and update views).
 */

const syncStatusConfig: { [key: string]: any } = {
  'not-started': { icon: 'fa-regular fa-hourglass-clock', text: 'Not Started' },
  'file-upload-completed': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'File Upload Completed' },
  'ready-for-ai-ingestion': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'Ready For AI Ingestion' },
  'ai-ingestion-in-progress': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'AI Ingestion Is In Progress' },
  'data-sync-completed': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'Data Sync Completed' },
  'file-storage-ingestion-in-progress': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'File Storage Ingestion Is In Progress' },
  'file-storage-updated': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'File Storage Updated' },
  'file-ingestion-in-progress': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'File Ingestion Is In Progress' },
  'file-storage-completed': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'File Storage Completed – Preparing for AI Ingestion' },
  'ai-ingestion-chunk-generation-in-progress': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'AI Ingestion Is In Progress' },
  'ai-ingestion-chunk-generation-completed': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'AI Ingestion Is In Progress' },
  'ai-ingestion-vector-generation-in-progress': { icon: 'fa-regular fa-hourglass-clock fa-spin-pulse', text: 'AI Ingestion Is In Progress' },
  'ai-ingestion-completed': { icon: 'fa-regular fa-octagon-check', text: 'AI Ingestion Completed' },
  'ai-ingestion-vector-generation-completed': { icon: 'fa-regular fa-octagon-check', text: 'AI Ingestion Completed' },
  'file-ingestion-failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'File Ingestion Failed', iconColor: 'var(--danger-color)' },
  'failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'Failed', iconColor: 'var(--danger-color)' },
  'ai-ingestion-token-limit-failure': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Token Limit Exceeded', iconColor: 'var(--danger-color)' },
  'file-storage-failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'File Storage Failed', iconColor: 'var(--danger-color)' },
  'file-storage-ingestion-failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Failed', iconColor: 'var(--danger-color)' },
  'ai-ingestion-chunk-generation-failure': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Failed', iconColor: 'var(--danger-color)' },
  'ai-ingestion-vector-generation-failure': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Failed', iconColor: 'var(--danger-color)' },
  'file-physically-missing': { icon: 'fa-regular fa-triangle-exclamation', text: 'File Missing', iconColor: 'var(--danger-color)' },
  'ai-ingestion-chunk-generation-failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Failed', iconColor: 'var(--danger-color)' },
  'ai-ingestion-vector-generation-failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Failed', iconColor: 'var(--danger-color)' },
  'ai-ingestion-token-limit-failed': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Token Limit Failed', iconColor: 'var(--danger-color)' },
  'project-file-missing': { icon: 'fa-regular fa-triangle-exclamation', text: 'Project File Missing', iconColor: 'var(--danger-color)' },
  'ai-ingestion-failed-for-scanned-pdf': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Failed For Scanned PDF', iconColor: 'var(--danger-color)' },
  'ai-ingestion-partially-failed-for-scanned-pdf': { icon: 'fa-regular fa-triangle-exclamation', text: 'AI Ingestion Partially Failed For Scanned PDF', iconColor: 'var(--danger-color)' }
};

const failedStatusList = ['file-ingestion-failed', 'failed', 'ai-ingestion-token-limit-failure', 'file-storage-failed', 'file-storage-ingestion-failed', 'ai-ingestion-chunk-generation-failure', 'ai-ingestion-vector-generation-failure', 'file-physically-missing', 'ai-ingestion-chunk-generation-failed', 'ai-ingestion-vector-generation-failed', 'ai-ingestion-token-limit-failed', 'project-file-missing', 'ai-ingestion-failed-for-scanned-pdf', 'ai-ingestion-partially-failed-for-scanned-pdf'];
const successStatusList = ['ai-ingestion-vector-generation-completed'];
let processStatus = '';
function getSyncStatusInfo(item: any) {
  return syncStatusConfig[item?.syncStatus]?.text && item?.isDeletable !== false && item?.isUpdatable !== false ? `<i style="color: ${syncStatusConfig[item?.syncStatus]?.iconColor};animation-duration: 2s;" class='${syncStatusConfig[item?.syncStatus]?.icon}'></i> ${syncStatusConfig[item?.syncStatus]?.text}` : ''
}

function findProcessStatus(item: any) {
  if (item?.syncStatus === 'not-started' && item?.isDeletable !== false && item?.isUpdatable !== false) {
    return processStatus = 'not-started'
  } else if (![...failedStatusList, ...successStatusList].includes(item?.syncStatus) && item?.isDeletable !== false && item?.isUpdatable !== false) {
    return processStatus = 'in-progress'
  } else if (!failedStatusList.includes(item?.syncStatus) && item?.isDeletable !== false && item?.isUpdatable !== false) {
    return processStatus = 'success'
  } else if (!successStatusList.includes(item?.syncStatus) && item?.isDeletable !== false && item?.isUpdatable !== false) {
    return processStatus = 'failed'
  }
  return ''
}

export const DATA_SOURCE_ENTITY_META_CONFIG: EntityConfigMetadata = {
  // --- Basic Entity Information ---
  name: 'DataSource',
  title: 'Data Sources',
  description: 'Data sources that your AI can connect to, such as databases, APIs, or files.',
  apiPath: 'data-sources',
  apiGetFilter: {
    'include': [
      {
        relation: 'projectFiles',
        scope: {
          fields: {
            createdDate: false,
            createdBy: false,
            createdById: false,
            isUpdatable: false,
            isDeleted: false,
            isDeletable: false,
            isPublic: false,
            type: false,
            path: false,
            id: false,
            projectId: false,
            provider: false,
            extension: false,
            parentFolderId: false,
            updatedDate: false,
            updatedBy: false,
            updatedById: false,
            saveToAssets: false,
            title: false
          }
        }
      },
      {
        relation: 'projectFile'
      },
      {
        relation: 'models'
      }
    ],
    'order': ['createdDate ASC']
  },
  dataFetchedFrom: 'loopback-api',
  titleFieldName: 'title',
  searchFields: ['title', 'name', 'connector', 'description'],
  subtitleFieldNames: ['connector', 'projectFile.title', 'isDataSynced', 'description'],
  listMode: ListMode.Card,
  alertConfig: (item: any) =>
    findProcessStatus(item) === 'not-started' ? {
      type: 'info',
      title: { content: 'AI Ingestion Not Started', type: 'h4' },
      description: 'The AI processing for this data source has not started yet.',
      showIcon: true
    } :
      findProcessStatus(item) === 'in-progress' ? {
        type: 'info',
        title: { content: 'AI Ingestion Is In Progress', type: 'h4' },
        description: 'Please wait while the process is being completed. This may take a few moments.',
        showIcon: true
      } : findProcessStatus(item) === 'failed' ? {
        type: 'error',
        title: { content: 'AI Ingestion Failed', type: 'h4' },
        description: 'AI Ingestion could not be completed.',
        showIcon: true
      } : findProcessStatus(item) === 'success' ? {
        type: 'success',
        title: { content: 'AI Ingestion Completed', type: 'h4' },
        description: 'File can be viewed and create, update, delete operations can be performed.',
        showIcon: true
      } : null
  ,
  getSubTitlesFunction: (item: any) => <HeadingComponentConfig[]>[
    {
      content: item?.connector ? `<i class='fa-regular fa-plug'></i> ${item?.connector}` : 'Unknown Connector',
      type: HeadingType.H6
    },
    {
      content: getSyncStatusInfo(item),
      type: HeadingType.H6
    },
    {
      content: item?.description ? `<i class='fa-regular fa-align-right'></i> ${item?.description}` : '',
      type: HeadingType.H6
    },
    {
      content: item?.projectFiles?.[0]?.name ? `<i class='fa-regular fa-file'></i> ${item?.projectFiles?.[0]?.name}` : '',
      type: HeadingType.H6
    },
    {
      content: item?.projectFiles?.[1]?.name ? `<i class='fa-regular fa-file'></i> ${item?.projectFiles?.[1]?.name}` : '',
      type: HeadingType.H6
    },
    {
      content: item?.projectFiles?.[2]?.name ? `<i class='fa-regular fa-file'></i> ${item?.projectFiles?.[2]?.name}` : '',
      type: HeadingType.H6
    },
    {
      content: (item?.projectFiles?.length - 3) >= 1 ? (`+${(item?.projectFiles?.length - 3)} more files` || '') : '',
      type: HeadingType.H6
    },
  ],
  getDescriptionFunction: () => null, // Hide description in card view - all info is shown in subtitles
  addNewItemCard: {
    title: 'Add New Data Source',
    description: 'Create a new data source for your AI to connect to.',
    iconName: 'fa fa-regular fa-plus',
  },
  // these actions will be used in the list view, if null, default actions will be used
  // listActions is empty because "Connect Your Data Source" button is already shown in the title bar
  // This prevents duplicate buttons in Grid view
  listActionsLength: 0,
  listActions: [],

  // --- Field Definitions ---
  fields: <EntityField[]>[
    {
      fieldName: 'title',
      title: 'Title',
      dataType: 'string',
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: true,
      formValidators: [
        {
          type: Validators.required,
          errorKey: 'required',
          errorMessage: 'Title is required.'
        }
      ],
      hint: 'A unique, descriptive name for the data source.'
    },
    {
      fieldName: 'name',
      title: 'Key',
      dataType: 'string',
      // The name will be a link to the detail view in the grid
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: true,
      formValidators: [
        {
          type: Validators.required,
          errorKey: 'required',
          errorMessage: 'Key is required.'
        },
        {
          type: Validators.pattern('^[a-zA-Z0-9_ ]+$'),
          errorKey: 'pattern',
          errorMessage: 'Key must be alphanumeric and can include spaces and underscores.'
        }
      ],
      hint: 'A unique, descriptive name for the data source.'
    },
    {
      fieldName: 'projectFiles',
      title: 'Files',
      dataType: 'string',
      isRequired: false
    },
    {
      fieldName: 'connector',
      title: 'Connector Type',
      dataType: 'string',
      disabled: true,
      // Display as a tag in the grid for easy identification
      displayFieldType: CrudViewColumnType.Tag,
      formFieldType: CrudViewFormItemType.Select,
      // The model has a default, implying a value is always present.
      isRequired: true,
      // Assuming the values from the DataSourceConnector enum.
      // These would be populated from your application's enum.
      selectOptions: [{ key: 'MongoDB', label: 'MongoDB', value: 'mongodb' },
      { key: 'PostgreSQL', label: 'PostgreSQL', value: 'postgresql' },
      { key: 'MSSQL', label: 'MSSQL', value: 'mssql' },
      { key: 'MySQL', label: 'MySQL', value: 'mysql' },
      { key: 'File', label: 'File', value: 'file' },
      { key: 'File Storage', label: 'File Storage', value: 'file-storage' },
      { key: 'Salesforce', label: 'Salesforce', value: 'salesforce' },
      { key: 'REST', label: 'REST', value: 'rest' },
      ],

      formValidators: [Validators.required],
      hint: 'Select the type of system this data source will connect to.'
    },
    {
      fieldName: 'description',
      title: 'Description',
      dataType: 'string',
      // Display as a text block in the grid
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextArea,
      isRequired: false,
      formValidators: [],
      inputDescription: 'A brief description of the data source.',
      hint: 'Provide a short description of this data source.'
    },
    {
      fieldName: 'config',
      title: 'Configuration (JSON)',
      dataType: 'object',
      // Display as a code block in the list view for a quick look
      displayFieldType: CrudViewColumnType.Code,
      // Use the Monaco Editor for a rich editing experience in forms
      formFieldType: CrudViewFormItemType.MonacoCodeEditor,
      default: {},
      isRequired: false,
      inputDescription: 'Enter the JSON configuration object required by the selected connector.',
      hint: 'For example: { "host": "localhost", "port": 27017, "database": "myDb" }'
    },
    {
      fieldName: 'createdDate',
      title: 'Created Date',
      dataType: 'date',
      // Display as a date in the grid
      displayFieldType: CrudViewColumnType.DateTime,
      formFieldType: null, // No form input for createdDate, as it is auto-generated
      isRequired: false,
      inputDescription: 'The date and time when this data source was created.',
      displayFormatFunction: createDateDisplayFormatFunction(CrudViewColumnType.DateTime)
    },
    {
      fieldName: 'createdBy',
      title: 'Created By',
      dataType: 'string',
      // Display as a text block in the grid
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: null, // No form input for createdBy, as it is auto-generated
      isRequired: false,
      inputDescription: 'The user who created this data source.',
    }
  ],
  // Custom Function that sets icons for each item for card view
  iconFn: (item: any) => {
    switch (item?.connector?.toLowerCase()) {
      case 'mongodb':
      case 'postgresql':
      case 'mysql':
      case 'oracle':
        return 'fa fa-regular fa-database fa-xl color-primary';
        break;
      case 'rest':
      case 'soap':
        return 'fa fa-regular fa-plug fa-xl';
        break;
      case 'file-storage':
        return 'fa fa-regular fa-file-lines fa-xl';
        break;
      default:
        return 'fa fa-regular fa-file fa-xl'; // Default icon for unknown connectors
    }
  },
  // This function adjusts the visibility of edit and delete buttons used on many grids.
  handleVisibilityFn: (data, item, action) => {
    if (item.isUpdatable && action.name === ItemActions.Edit) {
      return true;
    }

    if (item.isDeletable && action.name === ItemActions.Delete) {
      return true;
    }
  },
  // Your service supports relationsConfig, which can be defined here if needed.
  // Example:
  // relationsConfig: [
  //   {
  //     type: 'many-to-one',
  //     foreignKey: 'projectId',
  //     principalEntity: 'Project',
  //     principalKey: 'id'
  //   }
  // ]
};


export const DATA_SOURCE_CREATE_BY_FILE_META_CONFIG: EntityConfigMetadata = {
  name: 'CreateDataSourceByFile',
  title: 'Data Source',
  description: 'Upload a file (like CSV) and provide details to create a new data source for the AI to analyze.',
  // This apiPath should correspond to your custom endpoint
  apiPath: 'data-sources/create-by-file',
  dataFetchedFrom: 'loopback-api',
  titleFieldName: 'name',

  // --- Field Definitions for the Form ---
  fields: <EntityField[]>[
    {
      fieldName: 'file',
      title: 'Data File',
      dataType: 'file',
      // Use the FileUpload component for the file input
      formFieldType: CrudViewFormItemType.FileUpload,
      joyride: {
        joyrideStep: 'seventh',
        title: 'Upload Your File',
        text: 'Click here to upload your file. We have provided a sample CSV file for reference. Once you click “Next,” your selected file will automatically be uploaded.'
      },
      isRequired: true,
      formValidators: [
        {
          type: Validators.required,
          errorKey: 'required',
          errorMessage: 'File is required.'
        }
      ],
      config: {
        acceptedFileTypes: '.csv, .xlsx, .xls, .pdf, .docx, .txt, .json, .xml, .parquet',
        uploadText: 'Click or drag a file to this area to upload. The file will be processed by AI and data will be saved as a data source.',
        maxFileSize: 102400,
        maxFileCount: 5,
      },
      hint: 'Upload a file containing the data for this data source. Supported formats: CSV, Excel, PDF, Word, Text, JSON, XML, Parquet.',
      inputDescription: 'Select a file up to 100 MB in size to upload as the data source. The file will be processed by AI to extract relevant information. You can update the key and title to better describe the data source.',
    },
    {
      fieldName: 'title',
      title: 'Data Source Title',
      dataType: 'string',
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: true,
      formValidators: [
        {
          type: Validators.required,
          errorKey: 'required',
          errorMessage: 'File is required.'
        }
      ],
      hint: 'A human-readable title for the data source.',
    },
    {
      fieldName: 'key',
      title: 'Data Source Key',
      dataType: 'string',
      formFieldType: CrudViewFormItemType.TextBox,
      isRequired: true,
      hint: 'A unique, descriptive key for this data source.',
      formValidators: [
        {
          type: Validators.required,
          errorKey: 'required',
          errorMessage: 'Key is required.'
        },
        {
          type: Validators.pattern('^[a-zA-Z0-9_]+$'),
          errorKey: 'pattern',
          errorMessage: 'Key must be alphanumeric and can include underscores.'
        }
      ]
    },
    {
      isRequired: true,
      fieldName: 'categoryId',
      title: 'Category',
      formFieldType: CrudViewFormItemType.Select,
      selectOptions: [
      ],
      formValidators: [],
    },
    {
      isRequired: true,
      fieldName: 'ingestDataTo',
      title: 'Ingest data to:',
      formFieldType: CrudViewFormItemType.Radio,
      direction: 'vertical',
      options: [
        {
          key: 'Ingest to a new data table',
          value: 'ingestToAnewDataTable'
        },
        {
          key: 'Ingest to an existing data table',
          value: 'IngestToAnExistingDataTable'
        },
        {
          key: 'Ingest to the default data table of the data source',
          value: 'IngestToTheDefaultDataTableOfTheDataSource'
        }
      ],
      default: 'IngestToTheDefaultDataTableOfTheDataSource',
      formValidators: [],
    },
    {
      fieldName: 'selectProjectModel',
      title: 'Select Project Model',
      formFieldType: CrudViewFormItemType.Select,
      selectOptions: [
      ],
      default: 'IngestToTheDefaultDataTableOfTheDataSource',
      visible: false,
      formValidators: [],
    },
    {
      fieldName: 'description',
      title: 'Description',
      dataType: 'string',
      // Display as a text block in the grid
      displayFieldType: CrudViewColumnType.Text,
      formFieldType: CrudViewFormItemType.TextArea,
      isRequired: false,
      formValidators: [],
      inputDescription: 'A brief description of the data source.',
      hint: 'Provide a short description of this data source.'
    },
    {
      fieldName: 'isApproved',
      title: 'AI File Processing Approval',
      formFieldType: CrudViewFormItemType.CheckBox,
      default: true,
      isRequired: true,
      text: { text: 'By approving, you allow your files to be reviewed and processed by AI. This ensures they can be utilized in advanced search and analysis features.' },
    },
    // {
    //   fieldName: 'config',
    //   title: 'Data Source Configuration (Optional)',
    //   dataType: 'object',
    //   // The Monaco Editor is great for optional, structured JSON data
    //   formFieldType: CrudViewFormItemType.MonacoCodeEditor,
    //   isRequired: false,
    //   hint: 'Enter any additional JSON configuration for the data source.'
    // },
    // {
    //   fieldName: 'projectFileMetadata',
    //   title: 'File Metadata (Optional)',
    //   dataType: 'object',
    //   formFieldType: CrudViewFormItemType.MonacoCodeEditor,
    //   isRequired: false,
    //   hint: 'Enter any extra metadata for the file as a JSON array of key-value pairs.'
    // }
  ]
};

export const resetButton = {
  key: 'reset-ingestion',
  text: 'Reset Ingestion',
  icon: { name: 'fa fa-refresh' },
  tooltipText: 'Click this button to reset ingestion of the failed files.',
  behavior: ActionBehavior.Emit,
  titleBarView: ButtonTitleBarView.Dropdown,
  popconfirmTitle: 'Are you sure this action will reset the previously started ingestion process and then restart it?',
  emitWithoutPopconfirm: false
};

export const updateStatusToPartiallyCompleted = {
  key: 'update-status-to-partially-completed',
  text: 'Update Status to Partially Completed',
  icon: { name: 'fa-regular fa-square-right' },
  tooltipText: 'Click this button to update status to partially completed.',
  behavior: ActionBehavior.Emit,
  titleBarView: ButtonTitleBarView.Dropdown
};

export const addItemButton = {
  key: 'add-item',
  text: 'Add File',
  icon: { name: DEFAULT_CREATE_BUTTON_ICON },
  behavior: ActionBehavior.Emit
};

export const closeButtonIcon = {
  type: 'FONT_AWESOME',
  name: 'fa-solid fa-xmark',
  size: ButtonSize.ExtraSmall,
  customColorSettings: {
    backgroundColor: 'var(--component-header-background-color)',
    color: '#ffffff'
  },
  borderSettings: {
    borderColor: 'var(--component-header-background-color)'
  },
};
